// @ts-strict-ignore
import uniqBy from 'lodash/uniqBy';
import type { WorkflowState } from 'flow/store/redux/state';
import { extractActionTypeFromId } from '../../actions';
import type { WorkflowEdgeType, EdgeState, WorkflowEdge } from './types';
import { CanvasElementLayers } from '../layout/constants';
import type { WorkflowNode } from '../nodes';
import { SKELETON_NODE_ID } from '../nodes';
import type { NodeLineage } from '../layout';


export function isEdgeOfType<T extends WorkflowEdgeType>(
  edge: WorkflowEdge,
  type: T,
): edge is WorkflowEdge<T> {
  return edge.type === type;
}

/**
 * computes an id for an edge
 * @param sourceNodeId id of node from where the edge starts
 * @param targetNodeId id of node to where the edge ends
 * @returns id of the edge
 */
export function getEdgeId(
  sourceNodeId: WorkflowNode['id'],
  targetNodeId: WorkflowNode['id'],
): WorkflowEdge['id'] {
  return `${sourceNodeId}-${targetNodeId}`;
}

/**
 * compute the type of the edge.
 * extend this function in the future to support more edge types
 */
function getEdgeType(
  nodesById: Record<WorkflowNode['id'], WorkflowNode>,
  dependentId: string,
): WorkflowEdgeType {
  const dependencyNode = nodesById[dependentId];
  if (dependencyNode?.type === 'preview') return 'preview';

  return 'normal';
}

/**
 * evaluates wether or not an edge is connected to an outcome node
 */
export function isEdgeToOutcomeNode(targetId: string): boolean {
  const actionType = extractActionTypeFromId(targetId);
  return actionType === 'outcome';
}

/**
 * evaluates wether or not an edge is connected to a workflow routing node
 */
export function isEdgeToWorkflowRoutingNode(targetId: string): boolean {
  const actionType = extractActionTypeFromId(targetId);
  return actionType === 'workflow';
}

/**
 * Focused edges need a larger zIndex so that they are fully visible
 * when overlapped by other edge types
 */
export function getEdgeZIndex(edgeState: EdgeState): CanvasElementLayers {
  switch (edgeState) {
    case 'focused':
      return CanvasElementLayers.FOCUSED_EDGES;
    case 'deemphasized':
      return CanvasElementLayers.DEEMPHASIZED_EDGES;
    case 'faded':
      return CanvasElementLayers.FADED_EDGES;
    default:
      return CanvasElementLayers.EDGES;
  }
}


export function computeEdgeState({
  sourceId,
  targetId,
  uiStatePartial,
  nodeLineage,
}: {
  sourceId: WorkflowNode['id'];
  targetId: WorkflowNode['id'];
  uiStatePartial: {
    activeNodeEditorTab: WorkflowState['ui']['activeNodeEditorTab'];
    focusedNodeId: WorkflowState['ui']['focusedNodeId'];
    hoveredNodeId?: WorkflowState['ui']['hoveredNodeId'];
    isMoving?: WorkflowState['ui']['canvasProperties']['isMoving'];
  };
  nodeLineage: NodeLineage;
}): EdgeState {
  const {
    all,
    directIncomers,
    extendedIncomers,
    directOutgoers,
    extendedOutgoers,
  } = nodeLineage;

  const {
    focusedNodeId, activeNodeEditorTab, hoveredNodeId, isMoving,
  } = uiStatePartial;

  // These are the Edges drawn to the Nodes on Outcome or Workflow Routing Island
  if (isEdgeToOutcomeNode(targetId) || isEdgeToWorkflowRoutingNode(targetId)) {
    if (focusedNodeId === targetId) { return 'focused'; }
    if (hoveredNodeId === targetId && focusedNodeId !== targetId) return 'normal';
    if (isMoving || focusedNodeId !== targetId) return 'hidden';
  }

  // When we have a focused node, we only de-emphasize and focus edges when either
  // the thresholds or dependents tab is open for the context of parents/ancestors
  // or children/descendants, respectively. When the `options` panel is open, all
  // of the lineage falls through to the normal state.
  if (
    focusedNodeId
    && (
      (
        // If thresholds panel is open, we want to focus the incoming/parent relationships
        activeNodeEditorTab === 'rule'
        && targetId === focusedNodeId
      )
      || (
        // If the dependents panel is open, we want to focus the outgoing/child relationships
        activeNodeEditorTab === 'dependents'
        && sourceId === focusedNodeId
      )
    )
  ) return 'focused';

  if (
    focusedNodeId
    && (
      (
        // If rule panel is open, we want to de-emphasize all of the outgoing/child edges.
        // Edges to extended incomers will fall through to normal state
        activeNodeEditorTab === 'rule'
        && (
          directOutgoers.has(targetId)
          || extendedOutgoers.has(targetId)
        )
      )
      || (
        // If dependents panel is open, we want to de-emphasize all of the
        // incoming/parent edges. Edges to extended outgoers will fall through
        // to normal state
        activeNodeEditorTab === 'dependents'
        && (
          directIncomers.has(sourceId)
          || extendedIncomers.has(sourceId)
        )
      )
    )
  ) return 'deemphasized';

  if (
    all.size > 0
    && (
      !all.has(targetId)
      || !all.has(sourceId)
    )
  ) return 'faded';

  return 'normal';
}


export function buildSkeletonEdge(sourceNodeId: WorkflowNode['id']) {
  return buildEdge({
    id: getEdgeId(sourceNodeId, SKELETON_NODE_ID),
    source: sourceNodeId,
    target: SKELETON_NODE_ID,
    type: 'skeleton',
  });
}


export function buildEdge({
  id,
  source,
  target,
  type,
  data = null,
}: {
  id: WorkflowEdge['id'];
  source: WorkflowEdge['source'];
  target: WorkflowEdge['target'];
  type: WorkflowEdgeType;
  data?: WorkflowEdge['data'];
}): WorkflowEdge {
  return {
    id,
    source,
    target,
    type,
    data,
    /**
     * the zIndex of an edge depends on its state, which cannot be calculated during its instantiation
     * without having to iterate twice on the collection of elements. thus, the zIndex is initially
     * set to `0` and is later calculated in the layOutGraph function
     */
    zIndex: 0,
  };
}


export function buildEdges(
  dependencies: WorkflowState['workflowData']['dependencies'],
  nodes: WorkflowNode[],
): WorkflowEdge[] {
  if (!dependencies) return [];

  const edges: WorkflowEdge[] = [];
  const nodesById: Record<WorkflowNode['id'], WorkflowNode> = nodes
    .reduce((indexedNodes, node) => {
      indexedNodes[node.id] = node;
      return indexedNodes;
    }, {});

  Object
    .entries(dependencies)
    .forEach(([dependentId, dependenciesForDependentAction]) => {
      dependenciesForDependentAction.forEach(dependency => {
        /**
         * `isPreview` is set to `true` for dependencies created to preview new
         * thresholds on action link hover when viewing an action's thresholds
         * in the graph
         *
         * For preview dependencies we don't want to update the graph so we
         * return here and exclude the dependency from graph edges
         */
        if (dependency.isPreview) {
          return;
        }

        const edgeType = getEdgeType(nodesById, dependentId);
        const edge = buildEdge({
          id: getEdgeId(dependency.id, dependentId),
          source: dependency.id,
          target: dependentId,
          type: edgeType,
        });

        edges.push(edge);
      });
    });

  const importedDataNodes = nodes.filter(node => node.type === 'importedTailNode');
  importedDataNodes.forEach(node => {
    // todo - move this id extraction to the imported node helpers once we have them
    const targetNodeId = node.id.split('imported-tail-node__')[1];
    const tailEdge = buildEdge({
      id: getEdgeId(node.id, targetNodeId),
      source: node.id,
      target: targetNodeId,
      type: 'normal',
    });

    edges.push(tailEdge);
  });


  // todo sc-129099 - fix bug and remove the uniqBy
  return uniqBy(edges, 'id');
}

export function filterOutUnsetTagEdges(edges: WorkflowEdge[], unsetTagNodeIds: string[]) {
  return edges.filter(edge => (
    !unsetTagNodeIds.includes(edge.source) && !unsetTagNodeIds.includes(edge.target)
  ));
}

export function filterEdgesByLineage(edges: WorkflowEdge[], lineage: NodeLineage) {
  return edges.filter(edge => (
    lineage.all.has(edge.source) || lineage.all.has(edge.target)
  ));
}

/**
 * Returns a new list of edges where all instances of prevNodeId
 * has been replaced with newNodeId, regardless whether prevNodeId exists
 * as a target or source. Duplicate edges are removed.
 */
export function replaceNodesInEdges({ edges, prevIdToNewIdMap }: {
  edges: WorkflowEdge[];
  prevIdToNewIdMap: Record<WorkflowNode['id'], WorkflowNode['id']>;
}): WorkflowEdge[] {
  // do not iterate over edges if we have nothing to replace
  if (Object.keys(prevIdToNewIdMap).length === 0) {
    return edges;
  }

  const discoveredEdgeIds: Set<string> = new Set();

  return edges.reduce((builtEdges, edge) => {
    // do not transform edge if it does not exist in the transformation map
    if (!prevIdToNewIdMap[edge.source] && !prevIdToNewIdMap[edge.target]) {
      discoveredEdgeIds.add(edge.id);
      return [...builtEdges, edge];
    }

    const source = prevIdToNewIdMap[edge.source]
      ? prevIdToNewIdMap[edge.source]
      : edge.source;

    const target = prevIdToNewIdMap[edge.target]
      ? prevIdToNewIdMap[edge.target]
      : edge.target;

    const id = getEdgeId(source, target);

    if (discoveredEdgeIds.has(id)) {
      return builtEdges;
    }

    discoveredEdgeIds.add(id);
    const newEdge: WorkflowEdge = {
      ...structuredClone(edge),
      id,
      target,
      source,
    };

    return [...builtEdges, newEdge];
  }, []);
}
